import React, { useState } from 'react';
import { useFormState } from 'react-final-form';
import styled, { css } from 'styled-components';
import type { ThemeColor } from '~/theme/System/tokens/colorPalette';
import Button from '~/components/atom/Button';
import Input from '~/components/molecule/Input';
import { Field } from '~/components/organism/Forms';
import FormUtils from '~/components/organism/FormUtils';
import useCooldown from '~/hooks/useCooldown';
import getPhoneVerificationCooldownMs from '~/util/getPhoneVerificationCooldownMs';
import PhoneValidation from '~/util/Validation/Phone';
import { formatMinutesAndSeconds } from '~/util/time';
import type { Size } from '~/styles/constants';
import Catalog from '~/Catalog';
import useGlobalKeyBinding from '~/hooks/useGlobalKeyBinding';

const getResendCodeButtonLabel = (formattedCooldown: string) =>
  `Stuur code opnieuw over ${formattedCooldown}`;

const MAX_RESEND_LABEL_FOR_WIDTH = getResendCodeButtonLabel('00:00');

const text = {
  phoneLabel: 'Telefoonnummer',
  sendCodeButton: 'Stuur verificatiecode',
  resendCodeButton: 'Stuur code opnieuw',
  placeholder: '0612345678 / +31612345678',
  invalidPhone: Catalog.mfaInvalidDutchPhoneNumber,
};

type Props = {
  onSendCode: (phone: string) => Promise<boolean | void>;
  phoneError?: string | null;
  submitting?: boolean;
  sendingCode?: boolean;
  smsSent?: boolean;
  labelColor?: ThemeColor;
  fieldName?: string;
  size?: Size;
  hidePhoneInput?: boolean;
};

type FormValues = {
  phone?: string | null;
};

const PhoneVerificationField: React.FC<Props> = ({
  onSendCode,
  submitting = false,
  sendingCode = false,
  smsSent = false,
  labelColor,
  fieldName = 'phone',
  size = 'medium',
  hidePhoneInput = false,
}) => {
  const [, setSuccessfulSendCount] = useState(0);
  const { values } = useFormState<FormValues>({
    subscription: { values: true },
  });
  const {
    isActive: isResendCooldownActive,
    remainingMs: resendCooldownRemainingMs,
    startCooldown,
  } = useCooldown();

  const phoneValue = values[fieldName] ?? '';
  const formattedResendCooldown = formatMinutesAndSeconds(
    resendCooldownRemainingMs / 1000,
  );

  const sendCodeButtonLabel =
    smsSent && isResendCooldownActive
      ? getResendCodeButtonLabel(formattedResendCooldown)
      : smsSent
        ? text.resendCodeButton
        : text.sendCodeButton;

  const handleSendCode = async () => {
    if ((!hidePhoneInput && !phoneValue) || isResendCooldownActive) {
      return;
    }

    const sentSuccessfully = await onSendCode(phoneValue);
    if (!sentSuccessfully) {
      return;
    }

    setSuccessfulSendCount(previousSendCount => {
      const nextSendCount = previousSendCount + 1;
      startCooldown(getPhoneVerificationCooldownMs(nextSendCount));
      return nextSendCount;
    });
  };

  const isPhoneValid = PhoneValidation.isValidDutchPhoneNumber(phoneValue);

  const sendCodeButtonDisabled =
    sendingCode ||
    !phoneValue ||
    !isPhoneValid ||
    (smsSent && isResendCooldownActive);

  useGlobalKeyBinding({
    keys: 'enter',
    callback: () => void handleSendCode(),
    enabled: !hidePhoneInput && !sendCodeButtonDisabled,
  });

  if (hidePhoneInput) {
    return (
      <Button
        dataTestId="phone-verification-send-code-button"
        appearance="secondary"
        size={size}
        type="button"
        icon="send"
        disabled={sendingCode || (smsSent && isResendCooldownActive)}
        loading={sendingCode}
        label={sendCodeButtonLabel}
        onClick={() => void handleSendCode()}
      />
    );
  }

  const stableButtonLabel = MAX_RESEND_LABEL_FOR_WIDTH;

  return (
    <>
      <PhoneInputRow>
        <Field name={fieldName}>
          {({ input, meta: { error, touched, modified, submitFailed } }) => {
            const showValidation = touched || modified || submitFailed;
            const value = input.value ?? '';

            return (
              <Input
                {...input}
                value={value}
                onChange={event => {
                  input.onChange(
                    PhoneValidation.sanitizePhoneInput(event.target.value),
                  );
                }}
                size={size}
                label={{ text: text.phoneLabel, color: labelColor }}
                externalErrors={
                  showValidation
                    ? [
                        ...(error
                          ? [FormUtils.showError(error, showValidation)]
                          : []),
                        ...(value &&
                        !PhoneValidation.isValidDutchPhoneNumber(value)
                          ? [text.invalidPhone]
                          : []),
                      ]
                    : undefined
                }
                disabled={
                  submitting ||
                  sendingCode ||
                  (smsSent && isResendCooldownActive)
                }
                autoFocus
                autoComplete="tel"
                inputMode="tel"
                placeholder={text.placeholder}
              />
            );
          }}
        </Field>

        <SendCodeButton
          dataTestId="phone-verification-send-code-button"
          appearance="secondary"
          size={size}
          type="button"
          disabled={sendCodeButtonDisabled}
          loading={sendingCode}
          label={sendCodeButtonLabel}
          onClick={() => void handleSendCode()}
          icon="send"
          $minWidthLabel={stableButtonLabel}
        />
      </PhoneInputRow>
    </>
  );
};

const SendCodeButton = styled(Button)<{ $minWidthLabel: string }>(
  ({ $minWidthLabel, theme }) => css`
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    min-width: ${$minWidthLabel.length - 5}ch;

    ${theme.mq.lessThan('tablet')`
      width: 100%
    `}
  `,
);

const PhoneInputRow = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: ${theme.space('s')};
    width: 100%;

    & > :first-child {
      flex: 1 1 12rem;
      min-width: 0;
    }

    & > button {
      flex-shrink: 0;
    }
  `,
);

export default PhoneVerificationField;
